import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Plus } from "lucide-react";
import { api, errMsg, download, today } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Giornale, Mastrino, Verifica, Bilancio } from "@/components/coge/CogeViews";
import { ScritturaDialog } from "@/components/coge/ScritturaDialog";
import { PianoConti } from "@/components/coge/PianoConti";
import { LiquidazioneIva, Esercizi } from "@/components/coge/IvaEsercizi";
import { Registri, Reddito } from "@/components/coge/Regimi";
import { DateField } from "@/components/DateField";

const VIEWS = { giornale: Giornale, mastrino: Mastrino, verifica: Verifica, bilancio: Bilancio };

export const Contabilita = ({ base, kind, write, admin, regime }) => {
  const [meta, setMeta] = useState(null);
  const [p, setP] = useState({ dal: `${today().slice(0, 4)}-01-01`, al: today(), conto: "08.02" });
  const [rep, setRep] = useState(null);
  const [edit, setEdit] = useState(null);
  const loadMeta = useCallback(() => api.get(`${base}/piano`).then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e))), [base]);
  const load = useCallback(() => {
    if (!VIEWS[kind]) return;
    setRep(null);
    api.get(`${base}/report/${kind}`, { params: p }).then((r) => setRep({ ...r.data, kind })).catch((e) => toast.error(errMsg(e)));
  }, [base, kind, p]);
  useEffect(() => { loadMeta(); }, [loadMeta, kind]);
  useEffect(() => { load(); }, [load]);
  if (kind === "registri") return <Registri base={base} regime={regime} />;
  if (kind === "reddito") return <Reddito base={base} />;
  if (kind === "iva") return <LiquidazioneIva base={base} write={write} admin={admin} />;
  if (kind === "esercizi") return <Esercizi base={base} write={write} admin={admin} />;
  if (!meta) return null;
  if (kind === "piano") return <PianoConti meta={meta} admin={write && admin} onChanged={loadMeta} />;
  const nomi = Object.fromEntries(meta.piano.map((c) => [c.codice, c.nome]));
  const nuova = () => setEdit({ data: today(), causale: "SL", descrizione: "", righe: [{ conto: "", dare: "", avere: "" }, { conto: "", dare: "", avere: "" }] });
  const onEdit = (s) => setEdit(s.auto ? { data: s.data, causale: s.causale, descrizione: s.descrizione, rif_auto: s.id, righe: s.righe } : s);
  const del = async (s) => { if (!window.confirm("Eliminare la scrittura?")) return; try { await api.delete(`/coge/scritture/${s.id}`); toast.success("Scrittura eliminata"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const View = VIEWS[kind];
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="coge-filters">
        <label className="text-xs font-medium text-slate-600">Dal<DateField clearable className="mt-1 w-40" testid="coge-from" value={p.dal} onChange={(v) => setP({ ...p, dal: v })} /></label>
        <label className="text-xs font-medium text-slate-600">Al<DateField clearable className="mt-1 w-40" testid="coge-to" value={p.al} onChange={(v) => setP({ ...p, al: v })} /></label>
        {kind === "mastrino" && (
          <label className="text-xs font-medium text-slate-600">Conto
            <Select value={p.conto} onValueChange={(v) => setP({ ...p, conto: v })}><SelectTrigger className="mt-1 w-72" data-testid="coge-conto"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{meta.piano.map((c) => <SelectItem key={c.codice} value={c.codice}>{c.codice} · {c.nome}</SelectItem>)}</SelectContent></Select>
          </label>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" data-testid="coge-pdf" onClick={() => download(`${base}/report/${kind}`, { ...p, pdf: true }).catch((e) => toast.error(e.message))}><FileDown className="mr-2 h-4 w-4" />Stampa PDF</Button>
          {write && kind === "giornale" && <Button className="bg-sky-600 hover:bg-sky-700" data-testid="coge-new" onClick={nuova}><Plus className="mr-2 h-4 w-4" />Nuova scrittura</Button>}
        </div>
      </div>
      {rep?.kind === kind ? <View rep={rep} nomi={nomi} chiusi={meta.chiusi} onEdit={write ? onEdit : null} onDelete={del} /> : <p className="text-sm text-slate-500">Caricamento…</p>}
      {edit && <ScritturaDialog init={edit} meta={meta} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};
